import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError, type ConfirmedSession, type Setup } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Button, Field, TextInput } from "@/components/ui";
import { setLanguage, SUPPORTED_LANGUAGES, type Language } from "@/i18n";

export function authError(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (!(error instanceof ApiError)) return t("adminAuth.network");
  if (error.retryAfter)
    return t("adminAuth.retry", { seconds: error.retryAfter });
  const codes: Record<string, string> = {
    ADMIN_ACTIVATION_REQUIRED: "activationNeeded",
    INVALID_CREDENTIALS: "credentials",
    INVALID_CODE: "invalidCode",
    INVALID_RECOVERY_CODE: "invalidCode",
    CHALLENGE_EXPIRED: "expired",
    INVITE_EXPIRED: "inviteExpired",
    INVALID_INVITE: "inviteExpired",
    INVALID_PASSWORD: "passwordRules",
    RATE_LIMITED: "retryGeneric",
    ADMIN_CODE_REUSED: "codeReused",
    ADMIN_LOGIN_FAILED: "credentials",
    ADMIN_CODE_INVALID: "invalidCode",
    ADMIN_CHALLENGE_EXPIRED: "expired",
    ADMIN_PASSWORD_WEAK: "passwordWeak",
    ADMIN_INVITE_EXPIRED: "inviteExpired",
    ADMIN_RECOVERY_SETUP_REQUIRED: "recoveryRequired",
    ADMIN_ORIGIN_REJECTED: "originRejected",
    ADMIN_CSRF_REJECTED: "csrfRejected",
    ADMIN_INPUT_INVALID: "inputInvalid",
    ADMIN_RATE_LIMITED: "retryGeneric",
    ADMIN_AUTH_UNAVAILABLE: "network",
    ADMIN_ACCOUNT_UNAVAILABLE: "accountUnavailable",
    ADMIN_ALREADY_ACTIVATED: "alreadyActivated",
    ADMIN_ACCOUNT_CHANGED: "accountChanged",
    ADMIN_RESET_INVALID: "resetInvalid",
    ADMIN_REQUEST_FAILED: "network",
    ADMIN_RECOVERY_REQUIRED: "recoveryRequired",
    ADMIN_FACTOR_RECOVERY_PENDING: "factorRecoveryPending",
  };
  if (error.code && codes[error.code])
    return t(`adminAuth.${codes[error.code]}`);
  if (error.status === 429) return t("adminAuth.retryGeneric");
  if (error.status >= 500) return t("adminAuth.network");
  return t("adminAuth.failed");
}
function Frame({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  return (
    <main className="flex min-h-dvh items-center justify-center bg-chrome px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2">
          <span aria-hidden className="h-2 w-2 rounded-full bg-accent" />
          <span className="text-sm font-semibold text-white">
            {t("app.name")}
          </span>
        </div>
        <div className="card p-6">{children}</div>
        <div className="mt-4 flex justify-center gap-2">
          {SUPPORTED_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              onClick={() => setLanguage(language as Language)}
              aria-pressed={i18n.language === language}
              className="rounded-md px-3 py-2 text-xs font-medium uppercase text-white hover:bg-white/10"
            >
              {language}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
export function CodeField({
  code,
  setCode,
  recovery = false,
}: {
  code: string;
  setCode: (value: string) => void;
  recovery?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Field
      label={t(recovery ? "adminAuth.recoveryCode" : "adminAuth.authCode")}
    >
      {(id, describedBy) => (
        <TextInput
          id={id}
          aria-describedby={describedBy}
          type="text"
          inputMode={recovery ? "text" : "numeric"}
          pattern={recovery ? undefined : "[0-9]{6}"}
          maxLength={recovery ? 128 : 6}
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(event) =>
            setCode(
              recovery
                ? event.target.value
                : event.target.value.replace(/\D/g, ""),
            )
          }
        />
      )}
    </Field>
  );
}
export function RecoveryCodes({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [ack, setAck] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const text = codes.join("\n");
  function download() {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "evalyte-recovery-codes.txt";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-ink">
        {t("adminAuth.saveCodes")}
      </h1>
      <p className="text-sm text-ink-subtle">{t("adminAuth.codesHelp")}</p>
      <div className="rounded-lg border border-line bg-surface-sunken p-4 font-mono text-sm text-ink">
        {codes.map((code) => (
          <div key={code}>{code}</div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => {
            void navigator.clipboard
              .writeText(text)
              .catch(() => setCopyError(true));
          }}
        >
          {t("adminAuth.copy")}
        </Button>
        <Button onClick={download}>{t("adminAuth.download")}</Button>
      </div>
      {copyError && (
        <p role="alert" className="text-sm text-danger">
          {t("adminAuth.copyFailed")}
        </p>
      )}
      <label className="flex gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={ack}
          onChange={(event) => setAck(event.target.checked)}
        />
        {t("adminAuth.savedAck")}
      </label>
      <Button variant="primary" disabled={!ack} onClick={onDone}>
        {t("adminAuth.continue")}
      </Button>
    </div>
  );
}
export function SetupAuthenticator({
  setup,
  onConfirmed,
  onBack,
}: {
  setup: Setup;
  onConfirmed: (session: ConfirmedSession) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.setupConfirm(setup.setup_token, code);
      onConfirmed(result);
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-ink">
        {t("adminAuth.setupTitle")}
      </h1>
      <p className="text-sm text-ink-subtle">{t("adminAuth.setupHelp")}</p>
      <img
        className="mx-auto h-48 w-48 rounded-lg bg-white p-2"
        src={setup.qr_svg_data_url}
        alt={t("adminAuth.scanQr")}
      />
      <p className="break-all text-center font-mono text-sm text-ink">
        {setup.secret}
      </p>
      <Button
        onClick={() => {
          void navigator.clipboard
            .writeText(setup.secret)
            .catch(() => setError(t("adminAuth.copyFailed")));
        }}
      >
        {t("adminAuth.copySecret")}
      </Button>
      <form onSubmit={submit} className="space-y-4">
        <CodeField code={code} setCode={setCode} />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          disabled={code.length !== 6}
          className="w-full"
        >
          {t("adminAuth.confirm")}
        </Button>
      </form>
      <Button variant="ghost" onClick={onBack}>
        {t("adminAuth.back")}
      </Button>
    </div>
  );
}
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { complete } = useAuth();
  const [step, setStep] = useState<
    "password" | "code" | "setup" | "codes" | "forgot"
  >("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [setup, setSetup] = useState<Setup | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  function back() {
    setChallenge("");
    setCode("");
    setSetup(null);
    setConfirmed(null);
    setPassword("");
    setStep("password");
    setError(null);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (step === "password") {
        const result = await api.login(email.trim(), password);
        setPassword("");
        setChallenge(result.challenge_token);
        setStep("code");
      } else if (step === "code") {
        const result = await api.verify(challenge, code, recovery);
        setChallenge("");
        setCode("");
        if ("recovery_setup_required" in result) {
          setSetup(result);
          setStep("setup");
        } else complete(result);
      } else if (step === "forgot") {
        try {
          await api.forgotPassword(
            email.trim(),
            i18n.language.startsWith("en") ? "en" : "de",
          );
        } catch {
          /* Same response for unknown addresses. */
        }
        setForgotSent(true);
      }
    } catch (caught) {
      setError(authError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Frame>
      {step === "setup" && setup ? (
        <SetupAuthenticator
          setup={setup}
          onConfirmed={(value) => {
            setSetup(null);
            setConfirmed(value);
            setStep("codes");
          }}
          onBack={back}
        />
      ) : step === "codes" ? (
        <RecoveryCodes
          codes={confirmed?.recovery_codes ?? []}
          onDone={() => {
            if (confirmed) complete(confirmed);
            setConfirmed(null);
            setStep("password");
          }}
        />
      ) : (
        <>
          <h1 className="text-lg font-semibold text-ink">
            {t(
              step === "code"
                ? "adminAuth.codeTitle"
                : step === "forgot"
                  ? "adminAuth.forgotTitle"
                  : "login.title",
            )}
          </h1>
          <p className="mt-1 text-sm text-ink-subtle">
            {t(
              step === "code"
                ? "adminAuth.codeHelp"
                : step === "forgot"
                  ? "adminAuth.forgotHelp"
                  : "login.subtitle",
            )}
          </p>
          {forgotSent ? (
            <p role="status" className="mt-4 text-sm text-ink">
              {t("adminAuth.forgotSent")}
            </p>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              {step !== "code" && (
                <Field label={t("login.email")}>
                  {(id, describedBy) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  )}
                </Field>
              )}
              {step === "password" && (
                <Field label={t("login.password")}>
                  {(id, describedBy) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  )}
                </Field>
              )}
              {step === "code" && (
                <CodeField code={code} setCode={setCode} recovery={recovery} />
              )}
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={busy}
                disabled={
                  step === "code"
                    ? !code || (!recovery && code.length !== 6)
                    : !email || (step === "password" && !password)
                }
              >
                {t(
                  step === "code"
                    ? "adminAuth.confirm"
                    : step === "forgot"
                      ? "adminAuth.sendReset"
                      : "login.submit",
                )}
              </Button>
            </form>
          )}
          {step === "code" && (
            <button
              type="button"
              className="mt-3 text-sm text-brand"
              onClick={() => {
                setRecovery(!recovery);
                setCode("");
                setError(null);
              }}
            >
              {t(
                recovery
                  ? "adminAuth.useAuthenticator"
                  : "adminAuth.useRecovery",
              )}
            </button>
          )}
          <div className="mt-3">
            <button
              type="button"
              className="text-sm text-brand"
              onClick={() => {
                if (step === "password") {
                  setStep("forgot");
                  setForgotSent(false);
                } else {
                  back();
                  setForgotSent(false);
                }
              }}
            >
              {t(step === "password" ? "adminAuth.forgot" : "adminAuth.back")}
            </button>
          </div>
        </>
      )}
    </Frame>
  );
}
export { Frame };
